import type { Theme } from '@mui/material';
import { MONO_FONT } from './chromeTheme';

/**
 * Imaging-console chrome tokens. Colours are MUI theme **palette paths** (not
 * hex): they resolve against the always-dark `chromeTheme` that `ViewportGrid`
 * provides, so the palette can't drift and follows theme edits. Consume them as
 * `sx` values (`bgcolor: BAR_BG`, `borderColor: HAIRLINE`, `color: READOUT`).
 * One source of truth for the tile + grid chrome.
 */

/** Tabular monospace for every numeric readout. */
export const READOUT_FONT = MONO_FONT;

/** Pure-black viewport hero (theme-independent, never dimmed). */
export const CANVAS_BG = '#000';
/** Raised title / footer bars. */
export const CHROME_BG = 'background.paper';
/** Hover surface for interactive chrome. */
export const CHROME_BG_HOVER = 'action.hover';
/** Bar borders + hairline rules. */
export const HAIRLINE = 'divider';
/** Sunken fill of a console field the user reads or picks into. */
export const FIELD_BG = 'background.sunken';
/** Height of the title bar a pane is named in, in CSS pixels. */
export const TITLE_BAR_HEIGHT_PX = 27;
/** Gutter width between two tiles, in CSS pixels. */
export const GUTTER_PX = 4;
/**
 * Corner radius of the panes the viewport grid is made of, in CSS pixels. A
 * row's tiles and its footer bar form one pane: the tiles round only their top
 * corners and the footer only its bottom ones, so the two join without a
 * notch between them.
 */
const PANE_RADIUS_PX = 4;
/** The rounded top of a tile, square where it meets the footer. */
export const TILE_RADIUS = `${PANE_RADIUS_PX}px ${PANE_RADIUS_PX}px 0 0`;
/** The rounded bottom of a footer, square where it meets the tiles. */
const FOOTER_RADIUS = `0 0 ${PANE_RADIUS_PX}px ${PANE_RADIUS_PX}px`;
/** Side padding of a row footer, in theme spacing units. */
const FOOTER_PADDING_X = 1.5;
/** Vertical delimiter rule between title-bar / footer items. */
const DELIMITER = 'chrome.delimiter';
/** Bright readout text (plane labels, values). */
export const READOUT = 'text.primary';
/** Muted readout text (dates, series, secondary labels). */
export const READOUT_DIM = 'text.secondary';
/** Teal HUD labels (X/Y/Z, cursor). */
export const READOUT_ACCENT = 'primary.light';
/**
 * Caution accent: a state the user should check before trusting what a
 * pane shows, such as an oblique slice, an unapproved registration or a
 * primary row that differs from the primary image.
 */
export const WARNING = 'secondary.main';
/** Keyboard focus ring — bright, readable over dark chrome and bright canvas. */
export const FOCUS_RING = 'chrome.focusRing';
/**
 * Tile rings. Among the rings, teal marks the plane the user is on, so a
 * glance at the console finds it without reading a label:
 * - {@link TILE_OUTLINE} — the neutral hairline that frames every idle tile
 *   as its own pane.
 * - {@link HOVER_OUTLINE}: the teal ring for the hovered tile.
 */
export const TILE_OUTLINE = HAIRLINE;
export const HOVER_OUTLINE = 'primary.main';

/**
 * A frame over a tile's whole box: the hover ring, the keyboard focus
 * ring, the drag drop-zone. A WebGL canvas is a composited layer, so a `border`
 * or `outline` on an ancestor paints *under* it wherever the canvas has opaque
 * pixels. A positioned overlay paints above it, so the frame stays visible over
 * the image. Apply to a `position: relative` box; a pseudo-element also needs
 * its own `content`. The ring follows the tile's corners.
 */
export function tileRingSx(borderWidth: string, borderColor: string) {
    return {
        position: 'absolute' as const,
        inset: 0,
        pointerEvents: 'none' as const,
        border: `${borderWidth} solid`,
        borderColor,
        borderRadius: TILE_RADIUS,
    };
}

/**
 * A framed readout — the small bordered boxes that carry slice / HU / coordinate
 * values over the image. Translucent dark fill so values stay legible over both
 * bright anatomy and black background without hiding pixels.
 */
export const READOUT_BOX_SX = {
    fontFamily: READOUT_FONT,
    fontSize: 10,
    lineHeight: 1.35,
    color: READOUT,
    bgcolor: 'rgba(8, 12, 16, 0.66)',
    border: 1,
    borderColor: HAIRLINE,
    borderRadius: 0.75,
    px: 0.75,
    py: 0.25,
    whiteSpace: 'nowrap' as const,
    pointerEvents: 'none' as const,
};

/**
 * The raised bar under a row's tiles: the bottom of the pane the tiles
 * start, carrying the row's controls in one line of readout type.
 */
export const ROW_FOOTER_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    height: 40,
    px: FOOTER_PADDING_X,
    flexShrink: 0,
    bgcolor: CHROME_BG,
    borderRadius: FOOTER_RADIUS,
    fontFamily: READOUT_FONT,
    fontSize: 11,
    color: READOUT,
    fontVariantNumeric: 'tabular-nums',
};

/**
 * CSS custom property a tile's view holds the height of its bottom-left
 * corner stack in (the scale bar and the window histograms), so an overlay
 * placed in that corner can sit above the stack.
 */
export const TILE_CORNER_HEIGHT_VAR = '--tile-corner-height';

/**
 * Distance from a tile's bottom edge, as a CSS length, of an overlay placed
 * at the tile's bottom left: at least clear of the scale bar alone and of
 * the bottom edge's orientation letter, and otherwise just above the corner
 * stack, which sits 6 px from the bottom-left corner.
 */
export const ABOVE_TILE_CORNER_BOTTOM = `max(36px, calc(var(${TILE_CORNER_HEIGHT_VAR}, 0px) + 10px))`;

/** Distance of an orientation letter's chip from the image edge it names, px. */
export const EDGE_LETTER_INSET_PX = 6;

/** One orientation letter (A/P/R/L/H/F) in a small dark chip at an image edge. */
export const EDGE_CHIP_SX = {
    position: 'absolute' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 16,
    height: 16,
    px: 0.25,
    fontFamily: READOUT_FONT,
    fontSize: 10,
    fontWeight: 600,
    color: READOUT,
    bgcolor: 'rgba(0, 0, 0, 0.5)',
    pointerEvents: 'none' as const,
};

/**
 * A bare text control in a console bar: the readout's own type, brightening
 * on hover, with no box of its own.
 */
export const CONSOLE_TEXT_BUTTON_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    border: 'none',
    bgcolor: 'transparent',
    p: 0,
    color: READOUT,
    fontFamily: READOUT_FONT,
    fontSize: 11,
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': { color: READOUT_ACCENT },
};

/**
 * The segment at a row footer's right end: full height and flush with the
 * footer's edge, its left edge the rule that parts it from the bar, and its
 * corner rounded with the footer's, so its buttons read as the footer's own
 * corner.
 */
export const FOOTER_CORNER_SX = {
    display: 'flex',
    alignSelf: 'stretch',
    flexShrink: 0,
    mr: -FOOTER_PADDING_X,
    borderLeft: 1,
    borderColor: DELIMITER,
    borderBottomRightRadius: `${PANE_RADIUS_PX}px`,
    overflow: 'hidden',
};

/**
 * A button filling one square cell of the footer's corner segment, lit
 * across the whole cell while hovered and available.
 */
export const FOOTER_CORNER_BUTTON_SX = {
    ...CONSOLE_TEXT_BUTTON_SX,
    justifyContent: 'center',
    width: 40,
    height: '100%',
    fontSize: 16,
    lineHeight: 1,
    '&:hover:not(:disabled)': { bgcolor: CHROME_BG_HOVER },
};

/**
 * A console button while it is refused: dimmed, with a plain cursor. Place it
 * after the button's own style, so it outranks that style's hover colour.
 */
export const CONSOLE_DISABLED_SX = {
    '&:disabled': { color: READOUT_DIM, cursor: 'default' },
};

/** Class of the chevron in a bare console trigger, so the trigger can turn it. */
export const CONSOLE_CHEVRON_CLASS = 'console-chevron';

/**
 * The surface of a bare control in a console bar that sits beside framed
 * pickers: the pickers' height, room for the tint, and the chrome's hover
 * fill while hovered and available, or while its list is open.
 */
export const CONSOLE_HOVER_SURFACE_SX = {
    height: 28,
    pl: 1,
    // Shorter by the blank half of the chevron's box.
    pr: 0.5,
    borderRadius: 1,
    '&:hover:not(:disabled), &[aria-expanded="true"]': {
        bgcolor: CHROME_BG_HOVER,
        color: READOUT,
    },
    [`&[aria-expanded="true"] .${CONSOLE_CHEVRON_CLASS}`]: {
        transform: 'rotate(180deg)',
    },
};

/** The chevron of a bare console trigger: dim, and turning as its list opens. */
export const CONSOLE_CHEVRON_SX = {
    display: 'flex',
    flexShrink: 0,
    color: READOUT_DIM,
    transition: (theme: Theme) =>
        theme.transitions.create('transform', {
            duration: theme.transitions.duration.shorter,
        }),
};

/** Space between two controls side by side in a console bar, in spacing units. */
export const CONSOLE_CONTROL_GAP = 0.5;

/**
 * A run of items that read as one group inside a console bar: laid out on
 * one line, centred, never squeezed when the bar runs short of room.
 */
export function consoleGroupSx(gap: number) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        flexShrink: 0,
    };
}

/** The 1px rule parting two items inside a console bar or chip. */
export function consoleDividerSx(height: number | string) {
    return {
        width: '1px',
        height,
        bgcolor: DELIMITER,
        flexShrink: 0,
    };
}

/** Squares the corners of a framed control on the edge another one joins. */
export function squaredEdgeSx(side: 'left' | 'right') {
    return side === 'left'
        ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }
        : { borderTopRightRadius: 0, borderBottomRightRadius: 0 };
}

/**
 * A framed button joined on its `side` to another framed control, so the
 * two read as one: the corners of that edge squared, and the two frames
 * overlapping by their shared edge, which is the rule between them.
 */
export function joinedButtonSx(side: 'left' | 'right') {
    return {
        ...squaredEdgeSx(side),
        [side === 'left' ? 'ml' : 'mr']: '-1px',
        // Lifted while lit, or the neighbour's frame would hide the shared
        // edge's hover and focus colour.
        '&:hover, &.Mui-focusVisible': {
            position: 'relative' as const,
            zIndex: 1,
        },
    };
}

/**
 * The frame around a run of values that are dragged or typed in place in a
 * console bar: the framed pickers' height, fill, border and corners, so the
 * values read as one control beside them. Squared on the right while a
 * framed button joins it there.
 */
export function consoleFieldFrameSx(joinedOnRight: boolean) {
    return {
        ...consoleGroupSx(1),
        boxSizing: 'border-box' as const,
        height: 28,
        px: 1,
        bgcolor: FIELD_BG,
        border: 1,
        borderColor: HAIRLINE,
        borderRadius: 1,
        ...(joinedOnRight ? squaredEdgeSx('right') : {}),
    };
}

/**
 * A slider in a console bar or menu, `width` pixels long: a rail on the
 * hairline, and the track and thumb in the readout accent.
 */
export function consoleSliderSx(width: number) {
    return {
        width,
        padding: 0,
        '& .MuiSlider-rail': { opacity: 1, bgcolor: HAIRLINE },
        '& .MuiSlider-track': { border: 'none', bgcolor: READOUT_ACCENT },
        '& .MuiSlider-thumb': { bgcolor: READOUT_ACCENT },
    };
}

/** Size of the chevron that says a control opens a menu, in CSS pixels. */
export const TRIGGER_CHEVRON_SIZE_PX = 14;
